import { css } from '@emotion/react'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  Typography
} from '@mui/material'
import { Gender } from '@prisma/client'
import { AxiosError } from 'axios'
import { useRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import { useContext, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { match } from 'ts-pattern'
import { z, ZodError } from 'zod'
import { makeZodI18nMap } from 'zod-i18n-map'
import {
  CloseAbsoluteDiv,
  CloseIconContainer,
  ErrorMessageContainer,
  ModalBody
} from 'components/Auth/AuthModal.style'
import { CountryOptionContent } from 'components/Checkout/AttendeesForm/CountryOptionContent'
import { PhoneNumberInput } from 'components/Checkout/AttendeesForm/PhoneNumberInput'
import { PostalAddressInput } from 'components/Checkout/AttendeesForm/PostalAddressInput'
import { waitingListAdditionalFields } from 'components/Dashboard/WaitingLists/waitingListRaceConfig.utils'
import { BirthDateInput } from 'components/common/BirthDateInput/SafeBirthDateInput'
import { Button, ButtonVariant } from 'components/common/Button'
import { LogoButton } from 'components/common/Button/LogoButton'
import { Loader } from 'components/common/Loader'
import { LocaleContext } from 'components/common/LocaleContext'
import { Select } from 'components/common/Select/Select'
import { SelectWithSearch } from 'components/common/Select/SelectWithSearch'
import { InputErrorLabel, TextInput } from 'components/common/TextInput'
import {
  WaitingListAttendee,
  WaitingListAttendeeSchema
} from 'domain/models/WaitingLists/WaitingListsRace'
import { apiClient } from 'services/api/client'
import { LiveEventRace } from 'services/api/database/EventService'
import { useGetAttendeeProfile } from 'services/attendees/useGetAttendeeProfile'
import { media } from 'styles/responsive'

interface RegistrationFormProps {
  open: boolean
  onClose: () => void
  race: LiveEventRace
  confirmRegistration: (attendee: WaitingListAttendee) => void
}

type AdditionalFieldType = (typeof waitingListAdditionalFields)[number]

/**
 * Helper function to check if a field is required in the additional fields list
 */
const isFieldRequired = (
  fieldName: AdditionalFieldType,
  requiredFields: string[]
): fieldName is AdditionalFieldType => requiredFields.includes(fieldName)

const RegistrationForm = ({
  open,
  onClose,
  race,
  confirmRegistration
}: RegistrationFormProps) => {
  const { t } = useTranslation(['checkout', 'common', 'errors'])
  const { t: tErrors } = useTranslation('errors', { keyPrefix: 'validation' })
  const [isLoading, setIsLoading] = useState(false)
  const { locale } = useRouter()
  const { t: tRoot } = useTranslation()
  const { orderedCountries } = useContext(LocaleContext)
  const isRelay = race.raceSegments.length > 0
  const requiredAdditionalFields =
    race.raceWaitingListConfiguration?.additionalFields || []

  const methods = useForm<WaitingListAttendee>({
    resolver: zodResolver(
      WaitingListAttendeeSchema(isRelay, requiredAdditionalFields)
    )
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    watch,
    control,
    setError
  } = methods

  z.setErrorMap(makeZodI18nMap({ t: tRoot }))
  const [createAttendeeError, setCreateAttendeeError] = useState('')
  const { data: attendeeProfileFromUser } = useGetAttendeeProfile(open)
  const [isFormReset, setIsFormReset] = useState(false)
  useEffect(() => {
    if (attendeeProfileFromUser?.data && !isFormReset) {
      const {
        firstName,
        lastName,
        email,
        birthDate,
        nationality,
        gender,
        phoneNumber,
        postalAddressMain,
        postalAddressSecondary,
        postalAddressAreaLevel1,
        postalAddressAreaLevel2,
        postalAddressZipcode,
        postalAddressCity,
        postalAddressCountry
      } = attendeeProfileFromUser.data
      reset({
        firstName,
        lastName,
        email,
        particulars: {
          address: {
            postalAddressMain: postalAddressMain || undefined,
            postalAddressSecondary: postalAddressSecondary || undefined,
            postalAddressZipcode: postalAddressZipcode || undefined,
            postalAddressCity: postalAddressCity || undefined,
            postalAddressCountry: postalAddressCountry || undefined,
            postalAddressAreaLevel1: postalAddressAreaLevel1 || undefined,
            postalAddressAreaLevel2: postalAddressAreaLevel2 || undefined
          }
        },
        birthDate: birthDate ? z.coerce.date().parse(birthDate) : undefined,
        nationality: nationality || undefined,
        gender: gender || undefined,
        phoneNumber: phoneNumber || undefined
      })
      setIsFormReset(true)
    }
  }, [attendeeProfileFromUser?.data, isFormReset, reset])

  const handleClose = () => {
    onClose()
    setCreateAttendeeError('')
    clearErrors()
    reset()
    setIsFormReset(false)
  }

  const attendee = watch()

  const onSubmit = async (data: WaitingListAttendee) => {
    try {
      setCreateAttendeeError('')
      setIsLoading(true)
      await apiClient.post('/waiting-lists/add-attendee', {
        waitingListAttendee: data,
        raceId: race.id,
        language: locale,
        userId:
          data.email === attendeeProfileFromUser?.data?.email
            ? attendeeProfileFromUser.data.userId
            : undefined
      })
      confirmRegistration(attendee)
    } catch (error) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as
          | Record<string, unknown>
          | undefined
        if (
          errorData &&
          typeof errorData === 'object' &&
          'error' in errorData
        ) {
          const errorValue = errorData.error
          if (typeof errorValue === 'string') {
            match(errorValue)
              .with('ALREADY_ASSIGNED', (errorKey) => {
                setCreateAttendeeError(t(`race.waitingList.error.${errorKey}`))
              })
              .otherwise((errorMessage: string) => {
                setCreateAttendeeError(errorMessage)
              })
          } else if (errorValue && typeof errorValue === 'object') {
            const zodError = errorValue as ZodError
            setCreateAttendeeError(
              `${zodError.issues[0]?.path?.[0]?.toString() ?? ''}: ${
                zodError.issues[0]?.message ?? ''
              }`
            )
          } else {
            setCreateAttendeeError(error.message)
          }
        } else {
          setCreateAttendeeError(error.message)
        }
      } else {
        setCreateAttendeeError(t('attendeeProfile.snackbar.error.message'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle variant="h4">{t('race.waitingList.title')}</DialogTitle>
        <CloseAbsoluteDiv>
          <LogoButton
            onClick={handleClose}
            icon={<CloseIconContainer />}
            noBorder
          />
        </CloseAbsoluteDiv>
        <DialogContent dividers sx={{ p: 1 }}>
          <ModalBody
            css={css`
              gap: 0;
              ${media.maxWidth.sm} {
                padding: 8px;
              }
            `}
          >
            <Stack spacing={2}>
              <Typography variant="body2">
                <Trans
                  i18nKey="race.waitingList.form.description"
                  ns="checkout"
                  values={{ race: race.name }}
                  components={{ strong: <strong /> }}
                />
              </Typography>
              <Grid container spacing={{ xs: 1, md: 2 }}>
                {race.raceSegments.length > 0 && (
                  <Grid size={{ xs: 12 }}>
                    <TextInput
                      inputLabel={t('relay.team.title')}
                      errorMessage={errors.relayTeamName?.message}
                      inputProps={{
                        ...register('relayTeamName'),
                        style: { textTransform: 'capitalize' }
                      }}
                      fullSize
                      lowercase
                      noMargin
                    />
                  </Grid>
                )}

                <Grid size={{ xs: 12 }}>
                  <TextInput
                    fullSize
                    inputLabel={t('attendee.formFields.email')}
                    errorMessage={errors?.email?.message}
                    inputProps={{ ...register('email') }}
                    lowercase
                    noMargin
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <TextInput
                    fullSize
                    inputLabel={t('attendee.formFields.firstName')}
                    errorMessage={errors?.firstName?.message}
                    inputProps={{
                      ...register('firstName'),
                      style: { textTransform: 'capitalize' }
                    }}
                    lowercase
                    noMargin
                  />
                </Grid>

                <Grid size={{ xs: 6 }}>
                  <TextInput
                    fullSize
                    inputLabel={t('attendee.formFields.lastName')}
                    errorMessage={errors?.lastName?.message}
                    inputProps={{
                      ...register('lastName'),
                      style: { textTransform: 'capitalize' }
                    }}
                    lowercase
                    noMargin
                  />
                </Grid>

                {isFieldRequired('gender', requiredAdditionalFields) && (
                  <Grid size={{ xs: 6 }}>
                    <Select
                      noMargin
                      inputLabel={t('attendee.formFields.gender')}
                      errorMessage={errors?.gender?.message}
                      fieldName="gender"
                      placeholder={t('common:select')}
                      defaultValue=""
                      control={control}
                      options={Object.values(Gender).map((value) => ({
                        display: t(`enum.gender.${value}`),
                        value
                      }))}
                    />
                  </Grid>
                )}

                {isFieldRequired('birthDate', requiredAdditionalFields) && (
                  <Grid size={{ xs: 6 }}>
                    <BirthDateInput
                      inputLabel={t('attendee.formFields.birthDate')}
                      fieldName="birthDate"
                      errorMessage={errors?.birthDate?.message}
                      fullSize
                      noMargin
                    />
                  </Grid>
                )}
                {isFieldRequired('phoneNumber', requiredAdditionalFields) && (
                  <Grid size={{ xs: 6 }}>
                    <PhoneNumberInput
                      noMargin
                      inputLabel={t('attendee.formFields.phone')}
                      fieldName="phoneNumber"
                      fullSize
                      errorMessage={errors?.phoneNumber?.message}
                      setInvalidFormatError={() => {
                        setError('phoneNumber', {
                          type: 'custom',
                          message: tErrors('phoneNumber')
                        })
                      }}
                    />
                  </Grid>
                )}

                {isFieldRequired('nationality', requiredAdditionalFields) && (
                  <Grid size={{ xs: 6 }}>
                    <SelectWithSearch
                      noMargin
                      inputLabel={t('attendee.formFields.nationality')}
                      errorMessage={errors?.nationality?.message}
                      fieldName="nationality"
                      placeholder={t('common:select')}
                      options={orderedCountries.map((country) => ({
                        value: country.code,
                        display: (
                          <CountryOptionContent
                            countryName={country.name}
                            countryCode={country.code}
                          />
                        ),
                        stringToDisplay: country.name
                      }))}
                    />
                  </Grid>
                )}

                {isFieldRequired('postalAddress', requiredAdditionalFields) && (
                  <Grid size={{ xs: 12 }}>
                    <PostalAddressInput
                      inputLabel={t('attendee.formFields.address')}
                      placeholder={t('attendee.formFields.address')}
                    />
                  </Grid>
                )}
              </Grid>
            </Stack>
            <ErrorMessageContainer>
              {createAttendeeError && (
                <InputErrorLabel>{createAttendeeError}</InputErrorLabel>
              )}
            </ErrorMessageContainer>
          </ModalBody>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, padding: '16px 20px' }}
          css={css`
            ${media.maxWidth.sm} {
              padding: 16px 8px;
            }
          `}
        >
          <Stack width="100%">
            <Button
              label={t('race.waitingList.button.register')}
              fullWidth
              variant={ButtonVariant.Primary}
              type="submit"
              icon={isLoading ? <Loader /> : undefined}
              disabled={isLoading}
            />
          </Stack>
        </DialogActions>
      </form>
    </FormProvider>
  )
}

export default RegistrationForm
